import { watch, onMounted, onBeforeUnmount, nextTick, unref } from 'vue';
import { gsap, prefersReducedMotion } from '@/features/hr-workspace/utils/gsapOrg';
import {
  SIDEBAR_WIDTHS,
  SIDEBAR_MOTION
} from '@/features/hr-workspace/constants/sidebarLayout';

/**
 * Copy nodes revealed on expand. Keep in sync with CSS under
 * .sidebar-root--rail-layout and .sidebar-root--labels-pending.
 * Section titles are height-tweened separately (not in this list).
 */
const FADE_SEL = [
  '.sidebar-brand__copy',
  '.sidebar-nav-link__text',
  '.sidebar-nav-badge',
  '.sidebar-user-card__copy',
  '.sidebar-user-card__menu-icon'
].join(', ');

/** Expanded header padding — keep in sync with .sidebar-root__header CSS. */
const HEADER_PAD_EXPANDED = {
  minHeight: '4.875rem',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '0.875rem',
  paddingRight: '3.25rem'
};

/** Rail header padding — keep in sync with .sidebar-root--rail-layout .sidebar-root__header. */
const HEADER_PAD_RAIL = {
  minHeight: '7rem',
  paddingTop: '0.75rem',
  paddingBottom: '3.25rem',
  paddingLeft: 'var(--sidebar-rail-gutter)',
  paddingRight: 'var(--sidebar-rail-gutter)'
};
const SECTION_LABEL_SEL = '.sidebar-nav-section__label';
const SECTION_FOLLOW_SEL = '.sidebar-nav-section + .sidebar-nav-section';

const LABELS_PENDING_CLASS = 'sidebar-root--labels-pending';
const MOTION_LOCK_CLASS = 'sidebar-root--motion-lock';

/** Local alias — source of truth is SIDEBAR_MOTION (also drives content padding CSS). */
const MOTION = SIDEBAR_MOTION;

const CLEAR_PROPS = 'opacity,visibility';
const TOGGLE_CLEAR = 'opacity,visibility,pointerEvents';
const HEADER_CLEAR = 'minHeight,padding,paddingTop,paddingRight,paddingBottom,paddingLeft';
const SECTION_LABEL_CLEAR =
  'height,maxHeight,margin,marginTop,marginBottom,padding,paddingTop,paddingBottom,overflow,opacity,visibility';
const SECTION_GAP_CLEAR = 'marginTop';

/** Expanded section title box (matches CSS). */
const SECTION_LABEL_OPEN = {
  height: 'auto',
  marginBottom: '0.375rem',
  paddingTop: 0,
  paddingBottom: 0
};

/** Collapsed section title box (matches rail CSS). */
const SECTION_LABEL_CLOSED = {
  height: 0,
  marginTop: 0,
  marginBottom: 0,
  paddingTop: 0,
  paddingBottom: 0
};

const SECTION_GAP_EXPANDED = '0.875rem';
const SECTION_GAP_RAIL = '0.5rem';

/**
 * GSAP pin collapse/expand.
 *
 * Expand: rail off + labels-pending → width (t=0, sync with content padding) → fade labels.
 * Collapse: width shrink (t=0, sync with content padding) + fade labels in parallel → rail at end.
 * Toggle stays hidden while width/rail change; header grows via GSAP so the under-brand
 * slot opens smoothly, then toggle soft-fades in.
 * motion-lock hides the scrollbar during the tween so it does not vanish abruptly.
 *
 * Content padding-left uses SIDEBAR_MOTION.widthDurationMs / widthEasingCss in layout.css.
 *
 * @param {import('vue').Ref<HTMLElement|null>} sidebarRef
 * @param {{
 *   isCollapsed: import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>,
 *   isHoverExpanded: import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>,
 *   isMobile: import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>,
 *   isRailLayout: import('vue').Ref<boolean>
 * }} state
 */
export function useSidebarCollapseMotion(sidebarRef, state) {
  let activeTl = null;
  let hasMounted = false;

  const fadeNodes = (root) => {
    if (!root) return [];
    return Array.from(root.querySelectorAll(FADE_SEL));
  };

  const sectionLabels = (root) => {
    if (!root) return [];
    return Array.from(root.querySelectorAll(SECTION_LABEL_SEL));
  };

  const sectionFollowers = (root) => {
    if (!root) return [];
    return Array.from(root.querySelectorAll(SECTION_FOLLOW_SEL));
  };

  const toggleBtn = (root) => root?.querySelector?.('.sidebar-collapse-btn') ?? null;
  const headerEl = (root) => root?.querySelector?.('.sidebar-root__header') ?? null;

  const resetFade = (nodes) => {
    if (!nodes.length) return;
    gsap.set(nodes, { clearProps: CLEAR_PROPS });
  };

  const resetToggle = (btn) => {
    if (!btn) return;
    gsap.set(btn, { clearProps: TOGGLE_CLEAR });
  };

  const resetHeader = (header) => {
    if (!header) return;
    gsap.set(header, { clearProps: HEADER_CLEAR });
  };

  const resetSectionLabels = (labels) => {
    if (!labels.length) return;
    gsap.set(labels, { clearProps: SECTION_LABEL_CLEAR });
  };

  const resetSectionGaps = (followers) => {
    if (!followers.length) return;
    gsap.set(followers, { clearProps: SECTION_GAP_CLEAR });
  };

  const measureLabelHeight = (label) => {
    if (!label) return 0;
    const prev = {
      height: label.style.height,
      maxHeight: label.style.maxHeight,
      marginBottom: label.style.marginBottom,
      paddingTop: label.style.paddingTop,
      paddingBottom: label.style.paddingBottom,
      overflow: label.style.overflow,
      visibility: label.style.visibility,
      opacity: label.style.opacity
    };
    label.style.height = 'auto';
    label.style.maxHeight = 'none';
    label.style.marginBottom = SECTION_LABEL_OPEN.marginBottom;
    label.style.paddingTop = '0';
    label.style.paddingBottom = '0';
    label.style.overflow = 'hidden';
    label.style.visibility = 'hidden';
    label.style.opacity = '0';
    const h = label.getBoundingClientRect().height;
    label.style.height = prev.height;
    label.style.maxHeight = prev.maxHeight;
    label.style.marginBottom = prev.marginBottom;
    label.style.paddingTop = prev.paddingTop;
    label.style.paddingBottom = prev.paddingBottom;
    label.style.overflow = prev.overflow;
    label.style.visibility = prev.visibility;
    label.style.opacity = prev.opacity;
    return h > 0 ? h : 0;
  };

  /** Snap section titles + inter-section gaps to expanded or rail layout. */
  const snapSectionChrome = (root, { open }) => {
    const labels = sectionLabels(root);
    const followers = sectionFollowers(root);
    if (open) {
      if (labels.length) {
        gsap.set(labels, {
          ...SECTION_LABEL_OPEN,
          maxHeight: 'none',
          overflow: 'hidden',
          autoAlpha: 1
        });
        resetSectionLabels(labels);
      }
      if (followers.length) {
        gsap.set(followers, { marginTop: SECTION_GAP_EXPANDED });
        resetSectionGaps(followers);
      }
      return;
    }
    if (labels.length) {
      gsap.set(labels, {
        ...SECTION_LABEL_CLOSED,
        maxHeight: 0,
        overflow: 'hidden',
        autoAlpha: 0
      });
    }
    if (followers.length) {
      gsap.set(followers, { marginTop: SECTION_GAP_RAIL });
    }
  };

  const setMotionLock = (el, on) => {
    if (!el) return;
    el.classList.toggle(MOTION_LOCK_CLASS, !!on);
  };

  const showFade = (nodes) => {
    if (!nodes.length) return;
    gsap.set(nodes, { autoAlpha: 1 });
  };

  const hideFade = (nodes) => {
    if (!nodes.length) return;
    gsap.set(nodes, { autoAlpha: 0 });
  };

  const setLabelsPending = (el, on) => {
    if (!el) return;
    el.classList.toggle(LABELS_PENDING_CLASS, !!on);
  };

  const killTl = () => {
    if (!activeTl) return;
    activeTl.kill();
    activeTl = null;
    const el = sidebarRef.value;
    if (el) {
      setLabelsPending(el, false);
      setMotionLock(el, false);
      resetToggle(toggleBtn(el));
      resetHeader(headerEl(el));
      resetSectionLabels(sectionLabels(el));
      resetSectionGaps(sectionFollowers(el));
    }
  };

  const setWidth = (el, px) => {
    gsap.set(el, { width: px });
  };

  const setRail = (on) => {
    state.isRailLayout.value = !!on;
  };

  /**
   * @param {'expand'|'collapse'|'snapExpanded'|'snapRail'} mode
   * @param {{ animate?: boolean }} [opts]
   */
  const run = async (mode, opts = {}) => {
    const el = sidebarRef.value;
    if (!el) return;

    const animate = opts.animate !== false && !prefersReducedMotion();
    killTl();

    const btn = toggleBtn(el);
    const header = headerEl(el);

    if (mode === 'snapExpanded') {
      setLabelsPending(el, false);
      setMotionLock(el, false);
      setRail(false);
      await nextTick();
      setWidth(el, SIDEBAR_WIDTHS.expanded);
      const nodes = fadeNodes(el);
      showFade(nodes);
      resetFade(nodes);
      resetToggle(btn);
      resetHeader(header);
      snapSectionChrome(el, { open: true });
      return;
    }

    if (mode === 'snapRail') {
      setLabelsPending(el, false);
      setMotionLock(el, false);
      setRail(true);
      await nextTick();
      setWidth(el, SIDEBAR_WIDTHS.rail);
      hideFade(fadeNodes(el));
      resetToggle(btn);
      resetHeader(header);
      snapSectionChrome(el, { open: false });
      return;
    }

    if (mode === 'expand') {
      if (!animate) {
        setLabelsPending(el, false);
        setMotionLock(el, false);
        setRail(false);
        await nextTick();
        setWidth(el, SIDEBAR_WIDTHS.expanded);
        const nodes = fadeNodes(el);
        showFade(nodes);
        resetFade(nodes);
        resetToggle(btn);
        resetHeader(header);
        snapSectionChrome(el, { open: true });
        return;
      }

      if (btn) {
        gsap.set(btn, { autoAlpha: 0, pointerEvents: 'none' });
      }

      hideFade(fadeNodes(el));
      setLabelsPending(el, true);
      setMotionLock(el, true);

      // Keep expanded header padding while width opens — avoids HR mark jumping
      // from rail gutter → expanded padding mid-tween.
      if (header) {
        gsap.set(header, { ...HEADER_PAD_EXPANDED });
      }

      // Keep section titles collapsed until we measure + tween open (avoids icon jump).
      const labels = sectionLabels(el);
      const followers = sectionFollowers(el);
      if (labels.length) {
        gsap.set(labels, {
          ...SECTION_LABEL_CLOSED,
          maxHeight: 0,
          overflow: 'hidden',
          autoAlpha: 0
        });
      }
      if (followers.length) {
        gsap.set(followers, { marginTop: SECTION_GAP_RAIL });
      }

      setRail(false);
      await nextTick();

      const nodes = fadeNodes(el);
      hideFade(nodes);

      // Measure natural title heights while still closed, then tween open with width.
      const labelTargets = labels.map((label) => ({
        el: label,
        height: measureLabelHeight(label)
      }));
      if (labelTargets.length) {
        gsap.set(
          labelTargets.map((t) => t.el),
          {
            ...SECTION_LABEL_CLOSED,
            maxHeight: 0,
            overflow: 'hidden',
            autoAlpha: 0
          }
        );
      }

      activeTl = gsap.timeline({
        defaults: { overwrite: 'auto' },
        onComplete: () => {
          activeTl = null;
          setLabelsPending(el, false);
          setMotionLock(el, false);
          resetFade(nodes);
          resetToggle(btn);
          resetHeader(header);
          snapSectionChrome(el, { open: true });
        }
      });

      activeTl.to(el, {
        width: SIDEBAR_WIDTHS.expanded,
        duration: MOTION.widthDuration,
        ease: MOTION.widthEase
      }, 0);

      // Header already at expanded padding — no padding tween (prevents HR/button jump).

      labelTargets.forEach(({ el: label, height }) => {
        activeTl.to(label, {
          height,
          marginBottom: SECTION_LABEL_OPEN.marginBottom,
          maxHeight: height || 40,
          autoAlpha: 1,
          duration: MOTION.widthDuration,
          ease: MOTION.widthEase
        }, 0);
      });

      if (followers.length) {
        activeTl.to(followers, {
          marginTop: SECTION_GAP_EXPANDED,
          duration: MOTION.widthDuration,
          ease: MOTION.widthEase
        }, 0);
      }

      activeTl.add(() => {
        setLabelsPending(el, false);
      }, MOTION.labelFadeAt);

      if (nodes.length) {
        activeTl.to(nodes, {
          autoAlpha: 1,
          duration: MOTION.labelFade,
          ease: MOTION.labelFadeEase
        }, MOTION.labelFadeAt);
      }

      if (btn) {
        activeTl.to(btn, {
          autoAlpha: 1,
          duration: MOTION.toggleFade,
          ease: MOTION.toggleFadeEase,
          onStart: () => {
            gsap.set(btn, { pointerEvents: 'auto' });
          }
        }, MOTION.widthDuration * 0.85);
      }

      return;
    }

    // collapse — hide toggle immediately; grow header slot while width shrinks;
    // then rail chrome + soft toggle fade (no pop under HR mark).
    const nodes = fadeNodes(el);
    const labels = sectionLabels(el);
    const followers = sectionFollowers(el);
    setLabelsPending(el, false);

    if (!animate) {
      hideFade(nodes);
      setMotionLock(el, false);
      setRail(true);
      setWidth(el, SIDEBAR_WIDTHS.rail);
      resetToggle(btn);
      resetHeader(header);
      snapSectionChrome(el, { open: false });
      return;
    }

    if (btn) {
      gsap.set(btn, { autoAlpha: 0, pointerEvents: 'none' });
    }

    setMotionLock(el, true);

    // Capture current title heights before labels-pending / rail CSS can zero them.
    const labelFromHeights = labels.map((label) => ({
      el: label,
      height: label.getBoundingClientRect().height
    }));
    if (labelFromHeights.length) {
      labelFromHeights.forEach(({ el: label, height }) => {
        gsap.set(label, {
          height,
          overflow: 'hidden',
          maxHeight: 'none'
        });
      });
    }

    // Keep expanded header chrome for the whole width tween so HR mark + toggle
    // slot stay put. Rail layout (centered mark, under-brand toggle) applies at end.
    if (header) {
      gsap.set(header, { ...HEADER_PAD_EXPANDED });
    }

    activeTl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        activeTl = null;
        setLabelsPending(el, false);
        setMotionLock(el, false);
        resetToggle(btn);
        resetHeader(header);
        snapSectionChrome(el, { open: false });
      }
    });

    // Horizontal-only pending (link text collapse) — section title height is GSAP-owned.
    setLabelsPending(el, true);

    activeTl.to(el, {
      width: SIDEBAR_WIDTHS.rail,
      duration: MOTION.widthDuration,
      ease: MOTION.widthEase
    }, 0);

    if (nodes.length) {
      activeTl.to(nodes, {
        autoAlpha: 0,
        duration: MOTION.fadeOut,
        ease: MOTION.fadeOutEase
      }, 0);
    }

    labelFromHeights.forEach(({ el: label }) => {
      activeTl.to(label, {
        height: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
        autoAlpha: 0,
        duration: MOTION.widthDuration,
        ease: MOTION.widthEase
      }, 0);
    });

    if (followers.length) {
      activeTl.to(followers, {
        marginTop: SECTION_GAP_RAIL,
        duration: MOTION.widthDuration,
        ease: MOTION.widthEase
      }, 0);
    }

    // Switch to rail chrome at the end, then soft-fade the under-brand toggle.
    activeTl.add(() => {
      setRail(true);
      setLabelsPending(el, false);
      if (header) {
        gsap.set(header, { ...HEADER_PAD_RAIL });
      }
    }, MOTION.widthDuration);

    if (btn) {
      activeTl.to(btn, {
        autoAlpha: 1,
        duration: MOTION.toggleFade,
        ease: MOTION.toggleFadeEase,
        onStart: () => {
          gsap.set(btn, { pointerEvents: 'auto' });
        }
      }, MOTION.widthDuration + MOTION.toggleRevealDelay);
    }
  };

  onMounted(async () => {
    await nextTick();
    const el = sidebarRef.value;
    if (!el) return;

    const collapsed = unref(state.isCollapsed) && !unref(state.isHoverExpanded);
    setLabelsPending(el, false);
    setWidth(el, collapsed ? SIDEBAR_WIDTHS.rail : SIDEBAR_WIDTHS.expanded);
    setRail(collapsed);
    await nextTick();

    const nodes = fadeNodes(el);
    if (collapsed) {
      hideFade(nodes);
      snapSectionChrome(el, { open: false });
    } else {
      showFade(nodes);
      resetFade(nodes);
      snapSectionChrome(el, { open: true });
    }

    hasMounted = true;
  });

  watch(
    [
      () => unref(state.isCollapsed),
      () => unref(state.isHoverExpanded),
      () => unref(state.isMobile)
    ],
    async ([collapsed, hoverExpanded, mobile], prev) => {
      if (!hasMounted) return;

      const prevCollapsed = prev?.[0];
      const prevHover = prev?.[1];
      const prevMobile = prev?.[2];

      if (mobile) {
        killTl();
        setRail(false);
        const el = sidebarRef.value;
        if (el) {
          setLabelsPending(el, false);
          setMotionLock(el, false);
          resetFade(fadeNodes(el));
          resetToggle(toggleBtn(el));
          resetHeader(headerEl(el));
          snapSectionChrome(el, { open: true });
          gsap.set(el, { clearProps: 'width' });
        }
        return;
      }

      if (prevMobile && !mobile) {
        await run(collapsed ? 'snapRail' : 'snapExpanded', { animate: false });
        return;
      }

      // Hover expand/collapse — same motion as the pin button.
      if (hoverExpanded && !prevHover) {
        await run('expand');
        return;
      }

      if (prevHover && !hoverExpanded && collapsed) {
        await run('collapse');
        return;
      }

      if (collapsed && !prevCollapsed) {
        await run('collapse');
        return;
      }

      if (!collapsed && prevCollapsed) {
        // Already open from hover — pin without replaying expand.
        if (prevHover) {
          await run('snapExpanded', { animate: false });
          return;
        }
        await run('expand');
      }
    }
  );

  onBeforeUnmount(() => {
    killTl();
    const el = sidebarRef.value;
    if (el) {
      setLabelsPending(el, false);
      setMotionLock(el, false);
      resetFade(fadeNodes(el));
      resetToggle(toggleBtn(el));
      resetHeader(headerEl(el));
      resetSectionLabels(sectionLabels(el));
      resetSectionGaps(sectionFollowers(el));
      gsap.set(el, { clearProps: 'width' });
    }
  });

  return { run, killTl };
}
